import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiZoomIn } from 'react-icons/fi';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import MathBackground from '../components/layout/MathBackground';
import { API_BASE_URL } from '../utils/api';

const Gallery = () => {
    const [images, setImages] = useState([]);
    const [filteredImages, setFilteredImages] = useState([]);
    const [categories, setCategories] = useState(['All']);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedImage, setSelectedImage] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchGallery();
    }, []);

    useEffect(() => {
        if (selectedCategory === 'All') {
            setFilteredImages(images);
        } else {
            setFilteredImages(images.filter(img => img.category === selectedCategory));
        }
    }, [selectedCategory, images]);

    const fetchGallery = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/gallery/get_all.php`);
            const data = await response.json();
            if (data.status === 'success') {
                setImages(data.data);
                const uniqueCategories = ['All', ...new Set(data.data.map(img => img.category))];
                setCategories(uniqueCategories);
            }
        } catch (error) {
            console.error('Error fetching gallery:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white font-sans selection:bg-blue-500/30">
            <MathBackground />
            <Navbar />

            <div className="pt-32 pb-20 px-6 max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-16"
                >
                    <h1 className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-neutral-500 mb-6 tracking-tight">
                        Our Gallery
                    </h1>
                    <p className="text-neutral-400 max-w-2xl mx-auto text-lg leading-relaxed">
                        Explore moments from our classes, events, and student achievements.
                    </p>
                </motion.div>

                {/* Category Filter */}
                <div className="flex flex-wrap justify-center gap-4 mb-12">
                    {categories.map((category) => (
                        <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className={`px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all border ${selectedCategory === category
                                ? 'bg-blue-600 text-white border-blue-500 shadow-lg shadow-blue-500/25'
                                : 'bg-white/5 text-neutral-400 border-white/10 hover:bg-white/10 hover:text-white'
                                }`}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <motion.div layout className="columns-1 md:columns-2 lg:columns-5 gap-6 space-y-6">
                        <AnimatePresence>
                            {filteredImages.map((image, index) => (
                                <motion.div
                                    layout
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    transition={{ duration: 0.3 }}
                                    key={image.id}
                                    className="break-inside-avoid relative group rounded-2xl overflow-hidden cursor-zoom-in border border-white/5 bg-white/5 mb-6"
                                    onClick={() => setSelectedImage(image)}
                                >
                                    <img
                                        src={`${API_BASE_URL.replace('/api', '')}/${image.image_url}`}
                                        alt={image.title}
                                        className="w-full h-auto transform transition-transform duration-700 group-hover:scale-110"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                                        <h3 className="text-xl font-bold text-white mb-1">{image.title}</h3>
                                        {image.description && (
                                            <p className="text-sm text-neutral-300 line-clamp-2">{image.description}</p>
                                        )}
                                        <span className="inline-block mt-3 px-3 py-1 bg-blue-600/20 text-blue-400 text-xs font-bold uppercase tracking-wider rounded-full border border-blue-500/20 w-fit">
                                            {image.category}
                                        </span>
                                    </div>
                                    <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <FiZoomIn className="text-white" />
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}
            </div>

            <Footer />

            {/* Lightbox */}
            <AnimatePresence>
                {selectedImage && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[1001] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 cursor-zoom-out"
                        onClick={() => setSelectedImage(null)}
                    >
                        <button
                            className="absolute top-6 right-6 text-white text-4xl hover:text-blue-500 transition-colors z-[1002]"
                            onClick={() => setSelectedImage(null)}
                        >
                            <FiX />
                        </button>

                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center bg-[#0a0a0a] rounded-3xl overflow-hidden border border-white/10 shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={`${API_BASE_URL.replace('/api', '')}/${selectedImage.image_url}`}
                                alt={selectedImage.title}
                                className="w-full h-auto max-h-[75vh] object-contain bg-black"
                            />
                            <div className="p-8 w-full bg-[#0a0a0a]">
                                <h3 className="text-2xl font-bold text-white mb-2">{selectedImage.title}</h3>
                                {selectedImage.description && (
                                    <p className="text-neutral-400">{selectedImage.description}</p>
                                )}
                                <div className="mt-4 flex items-center gap-4 text-sm text-neutral-500">
                                    <span className="px-3 py-1 bg-white/5 rounded-full border border-white/10 uppercase tracking-wider text-xs font-bold text-blue-400">
                                        {selectedImage.category}
                                    </span>
                                    <span>Added on {new Date(selectedImage.created_at).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Gallery;
